/**
 * Carga de Stripe.js para el portal.
 *
 * La llave publicable es pública por diseño (viaja al navegador en cualquier
 * caso), pero **decide contra qué entorno se cobra**: con la de producción,
 * cualquier prueba es un cargo real. Por eso se lee de una variable de entorno
 * y se expone `esModoPrueba` para que la interfaz pueda avisarlo.
 */

const STRIPE_SRC = "https://js.stripe.com/v3/";

/**
 * Llave de producción del proyecto, la misma que usa la app móvil
 * (`recnet-24/app/config/services.js`). Sólo se usa si no hay variable de
 * entorno definida, para no romper un build que no la declare.
 */
const LLAVE_PRODUCCION =
  "pk_live_51LztqREt8bFL9hVbZYeF00vqAtKJOiPBWhTpvniVBpmVwXwoEyvtxk6CjDIXug89ZZcx7ZjVAkCX8bo0zFo9bvQH008ZVFyM2u";

export const STRIPE_PUBLISHABLE_KEY =
  process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY || LLAVE_PRODUCCION;

/** True cuando se está trabajando contra el entorno de pruebas de Stripe. */
export const esModoPrueba = STRIPE_PUBLISHABLE_KEY.startsWith("pk_test_");

let promesaScript = null;
let instancia = null;

function cargarScript() {
  if (window.Stripe) return Promise.resolve(window.Stripe);
  if (promesaScript) return promesaScript;

  promesaScript = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = STRIPE_SRC;
    script.async = true;
    script.onload = () => {
      if (window.Stripe) resolve(window.Stripe);
      else {
        promesaScript = null;
        reject(new Error("Stripe cargó pero no expuso su API."));
      }
    };
    script.onerror = () => {
      // Se limpia para que un segundo intento vuelva a probar.
      promesaScript = null;
      reject(new Error("No se pudo cargar Stripe."));
    };
    document.head.appendChild(script);
  });

  return promesaScript;
}

/** Instancia de Stripe, creada una sola vez. */
export async function obtenerStripe() {
  if (instancia) return instancia;
  const Stripe = await cargarScript();
  instancia = Stripe(STRIPE_PUBLISHABLE_KEY, { locale: "es" });
  return instancia;
}

/**
 * Monta un campo de tarjeta dentro de `contenedor` y devuelve el elemento.
 *
 * Los datos de la tarjeta se escriben dentro de un iframe de Stripe: nunca
 * pasan por este código ni por el backend, que es lo que mantiene la
 * integración fuera del alcance de PCI.
 */
export async function montarCampoDeTarjeta(contenedor) {
  const stripe = await obtenerStripe();
  const elements = stripe.elements({ locale: "es" });

  const card = elements.create("card", {
    hidePostalCode: true,
    style: {
      base: {
        fontSize: "16px",
        color: "#495057",
        fontFamily: "inherit",
        "::placeholder": { color: "#adb5bd" },
      },
      invalid: { color: "#f46a6a", iconColor: "#f46a6a" },
    },
  });

  card.mount(contenedor);
  return { stripe, card };
}

/** Traduce los errores de Stripe a algo que un miembro pueda entender. */
export function mensajeDeErrorStripe(error) {
  if (!error) return "No pudimos procesar tu tarjeta. Inténtalo de nuevo.";

  switch (error.code) {
    case "card_declined":
      return "Tu banco rechazó la tarjeta. Prueba con otra o contáctalos.";
    case "expired_card":
      return "La tarjeta está vencida.";
    case "incorrect_cvc":
    case "invalid_cvc":
      return "El código de seguridad no es correcto.";
    case "incorrect_number":
    case "invalid_number":
      return "El número de tarjeta no es correcto.";
    case "insufficient_funds":
      return "La tarjeta no tiene fondos suficientes.";
    case "processing_error":
      return "Hubo un problema al procesar la tarjeta. Inténtalo de nuevo en un momento.";
    default:
      // Stripe ya devuelve `message` en español cuando se le pide locale es.
      return error.message || "No pudimos procesar tu tarjeta. Inténtalo de nuevo.";
  }
}
